import { ElementRef, Inject, Injectable } from '@angular/core';
import { MainMapService } from './main-map.service';
import { WidgetNotifyService } from './widget-notify.service';

// Declare jQuery local variable
declare var $: any;

/**
 * @function extend
 * @description Extend jQuery with new animateCss function for event handling after
 * CSS animation finished
 */
$.fn.extend({
  animateCss: function (animationName, callback) {
    const animationEnd =
      'webkitAnimationEnd mozAnimationEnd MSAnimationEnd oanimationend animationend';
    this.addClass('animated ' + animationName).one(animationEnd, function () {
      $(this).removeClass('animated ' + animationName);
      if (callback) {
        callback();
      }
    });
    return this;
  },
});

/**
 * @function synchronizeScroll
 * @description Extend jQuery with new function for sync scrolling of two
 * Html Div-Elements
 */
$.fn.synchronizeScroll = function () {
  const elements = this;
  if (elements.length <= 1) {
    return;
  }

  elements.scroll(function () {
    const left = $(this).scrollLeft();
    const top = $(this).scrollTop();
    elements.each(function () {
      if ($(this).scrollLeft() !== left) {
        $(this).scrollLeft(left);
      }
      if ($(this).scrollTop() !== top) {
        $(this).scrollTop(top);
      }
    });
  });
};

/**
 * @copyright ESTA-GIS: Schweizerische Bundesbahnen SBB, 2017
 * @class DomUtilService
 * @version 0.0.1
 * @since November 2017
 * @description The DomUtilService provides globally util function for
 * manipulating DOM with jQuery
 */
@Injectable({
  providedIn: 'root',
})
export class DomUtilService {
  constructor(
    @Inject(MainMapService) private _mainMapService: MainMapService,
    @Inject(WidgetNotifyService) private _widgetNotifyService: WidgetNotifyService
  ) {}

  /* Common
 =======================  */

  public clearInputFieldById(id: string) {
    $('#' + id).val('');
  }

  /* MASSSTAB SLIDER & SCALE-BAR
  ======================  */

  public overrideScaleTooltip(item: ElementRef, scale: string) {
    const $scaleTooltip = $('#' + item.nativeElement.id)
      .find('.noUi-tooltip')
      .first();
    $scaleTooltip.text(scale);
  }

  /**
   *
   * @function showCoordinates
   * @param {ElementRef} item
   * @param {__esri.Point} point
   * @description Show mouse coordinates longitute/latitute on main-map
   */
  public showCoordinates(item: ElementRef, point: __esri.Point) {
    const $x = $('#' + item.nativeElement.id)
      .find('div.scalebar-easting-value')
      .first();
    const $y = $('#' + item.nativeElement.id)
      .find('div.scalebar-northing-value')
      .first();
    $x.text(Math.floor(point.x));
    $y.text(Math.floor(point.y));
  }

  /* DETAIL VIEW
  ====================  */

  /* DETAIL VIEW
  ====================  */

  /**
   * @function setFormInvalidBootstrapCSSFlag
   * @param {ElementRef} elem
   * @description Set whole form as invalid CSS flag
   */
  public setFormInvalidBootstrapCSSFlag(elem: ElementRef) {
    const $form = $(elem.nativeElement);
    $form.addClass('was-validated');
  }

  /**
   * @function setFieldInvalidBootstrapCSSFlag
   * @param {ElementRef} elem
   * @param invalidMessage
   * @description Set field as invalid css flag from bootstrap class
   */
  public setFieldInvalidBootstrapCSSFlag(elem: ElementRef, invalidMessage) {
    const elName = elem.nativeElement.name;
    // get input field for 1 and 2 columns
    const $identicalFields = $.find('input[name=' + elName + '],select[name=' + elName + ']');
    $identicalFields.forEach(field => {
      $(field).addClass('is-invalid');
    });

    // get invalid message box
    const $invalidMessageBox = $.find('div.invalid-tooltip.' + elName);
    $invalidMessageBox.forEach(msgBox => {
      $(msgBox).text(invalidMessage);
    });
  }

  /**
   * @function unSetFieldInvalidBootstrapCSSFlag
   * @param {ElementRef} elem
   * @description Remove invalid Bootstrap CSS Flag from field
   */
  public unSetFieldInvalidBootstrapCSSFlag(elem: ElementRef) {
    const elName = elem.nativeElement.name;
    const $identicalFields = $.find('input[name=' + elName + '],select[name=' + elName + ']');
    $identicalFields.forEach(field => {
      $(field).removeClass('is-invalid');
    });
  }

  /**
   * @name findLegendElements
   * @param node
   * @description Finds legend elements in Node
   */
  public findLegendElements(node: Node) {
    const $el = $(node);
    const query ='.esri-legend__layer-caption,.esri-legend__layer-cell--info';
    return $el.find(query);
  }

  /**
   * @name createLegendDiv
   * @description Creates a legende div
   * @returns {HTMLDivElement}
   */
  public createLegendDiv(): HTMLDivElement {
    const legendDiv = document.createElement('div');
    legendDiv.setAttribute('id', 'legend_1');
    return legendDiv;
  }

  /* ESTA-SWITCH
  ====================  */

  /**
   * @function isChecked
   * @param {ElementRef} elem
   * @description Check if checkbox is checked
   */
  public isChecked(elem: ElementRef): boolean {
    const $input = $(elem.nativeElement);
    return $input.is(':checked');
  }

  /* DYNAMIC WIDGET
  ====================  */

  /**
   * @function collapseWidget
   * @param {string} contentRefID
   * @param {__esri.Expand} widget
   * @param {string} animation
   * @description Collapse given widget with animate.css motion
   */
  public collapseWidget(contentRefID: string, widget: __esri.Expand, animation?: string) {
    const $domRef = $('#' + contentRefID);
    $domRef.removeClass('animation-on-tool-open');
    $domRef.animateCss(animation || 'animation-on-widget-collapse', () => {
      widget.collapse();
    });
  }

  /* SIDE-BAR
  ===================  */
  /**
   * @function requestFullScreen
   * @description Request fullscreen for IE11+ , Chrome and Firefox
   */
  public requestFullScreen() {
    /* document.body ist in ES6 typisiert als HTMLElement und hat kein msRequestFullscreen in typing.
     Um kein TypeScript compile Fehler zu bekommen muss ein any type gecastet werden da
     IE11+ das FullScren API msRequestFullscreen definiert. */
    const $body = document.body as any;
    // Supports most browsers and their versions.
    const requestMethod =
      $body.requestFullscreen || $body.webkitRequestFullScreen || $body.msRequestFullscreen;

    if (requestMethod) {
      requestMethod.call($body);
    }
  }
}
